<template>
  <div
    class="generalShadow relative mt-6 mb-3 px-8 py-6 overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 via-white to-white"
  >
    <!-- Decorative Background -->
    <div
      class="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-orange-100/60 blur-3xl"
    ></div>

    <div
      class="absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-orange-50 blur-3xl"
    ></div>

    <!-- Content -->
    <div class="relative z-10">
      <h1
        class="text-xl md:text-2xl font-bold text-orange-500 mb-3 tracking-tight"
      >
        {{ currentText.title }}
      </h1>

      <p
        class="text-sm text-neutral-600 leading-7 break-words overflow-hidden lg:pr-40"
      >
        {{ currentText.body }}
      </p>

      <div class="mt-5">
        <p class="text-sm font-semibold text-neutral-800 mb-3">
          {{ currentText.followDetails }}
        </p>

        <div class="flex flex-col gap-x-6 gap-y-2 text-sm text-neutral-700">
          <div class="flex items-center gap-2">
            <span class="text-orange-500">✓</span>
            <span> {{ currentText.followingItems.itemOne }}</span>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-orange-500">✓</span>
            <span> {{ currentText.followingItems.itemTwo }}</span>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-orange-500">✓</span>
            <span> {{ currentText.followingItems.itemThree }}</span>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-orange-500">✓</span>
            <span> {{ currentText.followingItems.itemFour }}</span>
          </div>

          <div class="flex items-center gap-2 md:col-span-2">
            <span class="text-orange-500">✓</span>
            <span> {{ currentText.followingItems.itemFive }}</span>
          </div>
        </div>
        <p
          class="text-sm text-neutral-800 pt-4 leading-7 break-words overflow-hidden lg:pr-40"
        >
          {{ currentText.followFooter }}
        </p>
      </div>
    </div>

    <!-- Illustration -->
    <div
      class="absolute hidden lg:block right-6 bottom-0 w-52 pointer-events-none opacity-90"
    >
      <img
        :src="require('@/assets/images/banner.png')"
        alt="Doctor Illustration"
        class="w-full h-auto object-contain"
      />
    </div>
  </div>
  <div
    class="generalShadow bg-white p-4 rounded-lg flex justify-between items-center gap-1"
  >
    <!-- Left Content -->
    <div class="space-y-2">
      <h3 class="text-lg text-orange-500 font-semibold">
        {{ currentText.sectionHeadertitle }}
      </h3>
      <p class="text-sm text-zinc-500 tracking-normal">
        {{ currentText.sectionHeaderMessage }}
      </p>
    </div>

    <!-- Right Circle Button -->
    <router-link
      to="/auth/support-center/information"
      rel="noopener noreferrer"
      class="w-12 h-12 bg-[#E87055] rounded-full flex items-center justify-center shrink-0 transition hover:scale-105 active:scale-95"
    >
      <RightSvg class="w-6 h-6 text-white" />
    </router-link>
  </div>

  <div class="py-6">
    <h2 class="text-orange-500 text-xl font-bold font-sans">
      {{ currentText.containerTitle }}
    </h2>
    <div class="py-4">
      <ul class="grid grid-cols-2 gap-4">
        <li v-for="(item, index) in itemCategories" :key="index">
          <!-- External link -->
          <a
            v-if="item.isUrl"
            :href="item.path"
            target="_blank"
            rel="noopener"
            class="block"
          >
            <div
              class="bg-white generalShadow rounded-md h-36 flex flex-col items-center justify-center space-y-2 transition hover:scale-105"
            >
              <span
                class="w-16 h-16 bg-[#E87055]/90 text-white rounded-full flex items-center justify-center"
              >
                <component :is="item.icon" height="32" width="32" />
              </span>

              <h3 class="text-orange-400 text-sm font-medium">
                {{ item.title }}
              </h3>
            </div>
          </a>

          <!-- Internal route -->
          <router-link v-else :to="item.path" class="block">
            <div
              class="bg-white generalShadow rounded-md h-36 flex flex-col items-center justify-center space-y-2 transition hover:scale-105"
            >
              <span
                class="w-16 h-16 bg-[#E87055]/90 text-white rounded-full flex items-center justify-center"
              >
                <component :is="item.icon" height="32" width="32" />
              </span>

              <h3 class="text-orange-400 text-sm font-medium text-center">
                <div>{{ item.title }}</div>
                <div class="text-xs">{{ item.subtitle }}</div>
              </h3>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useLanguage } from "@/utils/composables/useLanguage";

import ProfileSvg from "@/components/svgs/ProfileSvg.vue";
import MedicalSvg from "@/components/svgs/MedicalSvg.vue";
import MobileSvg from "@/components/svgs/MobileSvg.vue";
import RightSvg from "@/components/svgs/RightSvg.vue";
import FileSvg from "@/components/svgs/FileSvg.vue";
import ContactSvg from "@/components/svgs/ContactSvg.vue";

export default {
  components: {
    ProfileSvg,
    MedicalSvg,
    MobileSvg,
    RightSvg,
    FileSvg,
    ContactSvg,
  },

  setup() {
    const route = useRoute();
    const { currentText } = useLanguage();

    // Reload only once when this page is opened
    onMounted(() => {
      const reloadKey = `reloaded-${route.fullPath}`;

      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "true");
        window.location.reload();
      } else {
        sessionStorage.removeItem(reloadKey);
      }
    });

    const itemCategories = computed(() => [
      {
        id: 1,
        title: currentText.value.sectionItemCategory.categoryOne,
        subtitle: "",
        icon: "ProfileSvg",
        isUrl: false,
        path: "/auth/policy/detail",
      },
      {
        id: 2,
        title: currentText.value.sectionItemCategory.categoryTwo,
        subtitle: `(${currentText.value.sectionItemCategory.subcategoryTwo})`,
        icon: "MedicalSvg",
        isUrl: false,
        path: "/auth/request/loa",
      },
      {
        id: 3,
        title: currentText.value.sectionItemCategory.categoryThree,
        subtitle: "",
        icon: "FileSvg",
        isUrl: false,
        path: "/auth/request/loa/transaction",
      },
      {
        id: 4,
        title: currentText.value.sectionItemCategory.categoryFour,
        subtitle: "",
        icon: "MobileSvg",
        isUrl: true,
        path: "https://telemed.iwc.com.ph/#/",
      },
      {
        id: 5,
        title: currentText.value.sectionItemCategory.categoryFive,
        subtitle: "",
        icon: "ContactSvg",
        isUrl: false,
        path: "/auth/contact/detail",
      },
    ]);

    return {
      currentText,
      itemCategories,
    };
  },
};
</script>
